import { PublicRoutes } from "@/constants/routes";
import { Archive, CalendarClock, Download, Trash2 } from "lucide-react";
import { ACCOUNT_TERMS, DPO } from "./company";

const { readOnlyDays, retentionDays } = ACCOUNT_TERMS;
import type { LegalDocument } from "./types";

export const DATA_RETENTION: LegalDocument = {
  key: "retencao",
  path: PublicRoutes.RETENCAO_DADOS,
  eyebrow: "Proteção de dados",
  title: "Retenção de dados",
  seoTitle: "Retenção de dados: por quanto tempo guardamos",
  description:
    "Por quanto tempo a Fidexa guarda cada tipo de dado, com o fundamento legal de cada prazo, e o que acontece com os dados da loja quando o contrato termina.",
  intro:
    "Guardar dado além do necessário é risco para todo mundo. Aqui está, tipo a tipo, por quanto tempo cada dado fica com a Fidexa e por qual motivo legal.",
  version: "1.0",
  updatedAt: "2026-10-12",

  highlights: [
    {
      icon: CalendarClock,
      title: "Só o tempo necessário",
      text: "Cada dado fica pelo tempo da finalidade que justificou a coleta, ou pelo prazo que a lei exige.",
    },
    {
      icon: Download,
      title: `${readOnlyDays} dias para a cópia`,
      text: `Depois do fim do acesso, a plataforma fica disponível para leitura por ${readOnlyDays} dias e a loja pode pedir a cópia por e-mail.`,
    },
    {
      icon: Trash2,
      title: `Eliminação em ${retentionDays} dias`,
      text: `Os dados ficam guardados e bloqueados até completar ${retentionDays} dias do fim do acesso e, então, são eliminados.`,
    },
    {
      icon: Archive,
      title: "Prazos com fundamento",
      text: "Quando guardamos algo por mais tempo, citamos a lei que obriga.",
    },
  ],

  sections: [
    {
      id: "regra-geral",
      title: "A regra geral",
      summary:
        "O dado é eliminado quando a finalidade termina. Só fica guardado depois disso o que a lei manda guardar.",
      blocks: [
        {
          type: "paragraph",
          text: "Pela LGPD (art. 15), o tratamento termina quando a finalidade é alcançada, quando o prazo acaba, quando o titular revoga o consentimento ou quando a ANPD determina. Depois disso, o dado só pode ser conservado nas hipóteses do art. 16, como o cumprimento de obrigação legal, ou em forma anonimizada, sem identificar ninguém.",
        },
        {
          type: "paragraph",
          text: "Os prazos abaixo valem para a Fidexa. Sobre os dados dos clientes das lojas, a Fidexa é operadora: a loja, como controladora, pode definir prazos menores e pedir a eliminação antes.",
        },
      ],
    },
    {
      id: "prazos",
      title: "Prazo de cada tipo de dado",
      summary:
        "Dados das lojas ficam enquanto durar o contrato. Registros de acesso, 6 meses. Documentos fiscais, 5 anos.",
      blocks: [
        {
          type: "table",
          caption: "Prazos de retenção por tipo de dado",
          columns: [
            { key: "dado", header: "Tipo de dado", primary: true },
            { key: "prazo", header: "Por quanto tempo" },
            { key: "fundamento", header: "Fundamento" },
          ],
          rows: [
            {
              dado: "Dados dos clientes das lojas (cadastro, parcelas, conversas e anexos)",
              prazo: `Enquanto o contrato estiver em vigor. Depois, ${readOnlyDays} dias para leitura e cópia, guarda bloqueada até ${retentionDays} dias e eliminação.`,
              fundamento:
                "Instruções da loja controladora (LGPD, art. 39) e contrato de licenciamento.",
            },
            {
              dado: "Cadastro do lojista e dos usuários",
              prazo:
                "Enquanto a conta estiver ativa. Depois, só os dados do contrato, por 5 anos.",
              fundamento:
                "Execução do contrato e prazos de prescrição (Código Civil, art. 206, §5º, I; CDC, art. 27).",
            },
            {
              dado: "Notas fiscais e dados financeiros",
              prazo:
                "5 anos, contados do primeiro dia do ano seguinte ao do documento.",
              fundamento: "Código Tributário Nacional, arts. 173, 174 e 195.",
            },
            {
              dado: "Registros de acesso à plataforma (IP, data e hora)",
              prazo:
                "6 meses, ou mais se houver ordem judicial ou requisição de autoridade.",
              fundamento: "Marco Civil da Internet, art. 15.",
            },
            {
              dado: "Pedidos de demonstração e contatos comerciais",
              prazo: "Até 24 meses sem interação, ou até você pedir para sair.",
              fundamento:
                "Legítimo interesse (LGPD, art. 7º, IX) e término do tratamento (art. 15).",
            },
            {
              dado: "Pedidos de titulares e nossas respostas",
              prazo: "5 anos.",
              fundamento:
                "Prestação de contas (LGPD, art. 6º, X) e defesa em processos (art. 7º, VI).",
            },
            {
              dado: "Registro de incidentes de segurança",
              prazo: "5 anos, no mínimo.",
              fundamento: "Resolução CD/ANPD nº 15/2024.",
            },
            {
              dado: "Cookies do site",
              prazo:
                "Pela validade de cada cookie. Você pode apagá-los quando quiser no navegador.",
              fundamento: `Consentimento, para os de análise e anúncios. Veja a [Política de Privacidade](${PublicRoutes.POLITICA_PRIVACIDADE}#cookies).`,
            },
          ],
        },
      ],
    },
    {
      id: "fim-do-contrato",
      title: "Quando o contrato termina",
      summary: `A loja tem ${readOnlyDays} dias para consultar os dados e pedir a cópia. Depois, os dados ficam bloqueados até completar ${retentionDays} dias e são eliminados.`,
      blocks: [
        {
          type: "list",
          ordered: true,
          items: [
            `**Leitura e cópia:** por ${readOnlyDays} dias depois do fim do acesso, a plataforma fica disponível só para leitura e a loja pode pedir uma cópia dos dados, enviada ao e-mail do responsável pela conta. Baixar e guardar essa cópia é responsabilidade da loja; passado o prazo, não entregamos mais cópias.`,
            `**Guarda bloqueada:** do ${readOnlyDays + 1}º ao ${retentionDays}º dia, os dados ficam guardados e bloqueados (LGPD, art. 5º, XIII), sem acesso da loja e sem uso para outra finalidade, só para comprovar o que foi feito em caso de reclamação, disputa ou pedido de autoridade (art. 7º, VI) e para investigar fraude ou incidente. **Eliminação:** ao completar ${retentionDays} dias, eliminamos os dados de forma definitiva e irrecuperável.`,
            "**O que pode ficar:** só o que a lei obriga a guardar, como notas fiscais e registros de acesso, pelos prazos da tabela acima.",
          ],
        },
      ],
    },
    {
      id: "copias-de-seguranca",
      title: "Cópias de segurança",
      summary:
        "As cópias de segurança são substituídas em ciclo. Um dado eliminado da plataforma some também das cópias ao fim desse ciclo.",
      blocks: [
        {
          type: "paragraph",
          text: "Mantemos cópias de segurança em um provedor separado dos servidores, para recuperar a plataforma em caso de falha. Elas são sobrescritas periodicamente. Por isso, um dado eliminado da plataforma ainda pode existir em uma cópia antiga até que ela seja substituída. Enquanto isso, a cópia fica protegida e não é usada para nenhuma outra finalidade.",
        },
      ],
    },
    {
      id: "pedir-exclusao",
      title: "Como pedir a exclusão antes do prazo",
      summary:
        "Escreva para o encarregado. Se o dado for de uma loja, encaminhamos o pedido a ela.",
      blocks: [
        {
          type: "paragraph",
          text: `Para pedir a eliminação de dados seus, escreva para [${DPO.email}](mailto:${DPO.email}). Se for um dado em que a Fidexa é controladora, eliminamos e confirmamos em até 15 dias, exceto o que a lei obrigar a guardar; nesse caso, dizemos qual é a obrigação. Se for um dado que uma loja cadastrou, encaminhamos o pedido à loja em até 5 dias úteis.`,
        },
      ],
    },
  ],

  changelog: [
    {
      version: "1.0",
      date: "2026-10-12",
      changes: ["Primeira versão publicada."],
    },
  ],
};
